import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { MeterConsumptionGranularityTypes } from '@app/shared/enums';
import { map, range, isNil, rangeRight, last, first } from 'lodash';
import { MeterConsumptionGranularityKeys, MeterDownloadWizardFilter } from '@app/shared/models/meter/meter-download-wizard-data';
import { ISODateTimeStartOfDayFormat } from '@app/shared/constants';

@Component({
  selector: 'app-date-selection',
  templateUrl: './date-selection.component.html',
  styleUrls: ['./date-selection.component.scss'],
})
export class DateSelectionComponent implements OnInit {
  @Input() filter: MeterDownloadWizardFilter = {} as MeterDownloadWizardFilter;
  @Output() title = new EventEmitter<string>();
  @Output() nextButtonStatus = new EventEmitter<boolean>();

  @Input() set startDate(value) {
    this._startDate = value ? moment(value).format(ISODateTimeStartOfDayFormat) : null;

    this.isStartDateEmpty = this.isEmpty(this._startDate);
    this.getNextButtonStatus();

    this.filter.dateRange.startDate = this._startDate;
    this.resetDateFilter();
  }

  @Input() set endDate(value) {
    this._endDate = value ? moment(value).format(ISODateTimeStartOfDayFormat) : null;

    this.isEndDateEmpty = this.isEmpty(this._endDate);
    this.getNextButtonStatus();

    this.filter.dateRange.endDate = this._endDate;
    this.resetDateFilter();
  }

  get startDate() {
    return this._startDate;
  }
  get endDate() {
    return this._endDate;
  }

  private _startDate: string | null = null;
  private _endDate: string | null = null;
  isStartDateEmpty = true;
  isEndDateEmpty = true;

  minDate = moment(new Date()).subtract(5, 'years');
  maxDate = moment(new Date());

  useDatePicker = false;
  useMonthYearDropdown = false;
  useWeekYearDropdown = false;
  useYearDropdown = false;
  useFixedYearRange = false;

  startWeekOptions: string[] = [];
  startMonthOptions: string[] = [];
  startYearOptions: string[] = [];
  endWeekOptions: string[] = [];
  endMonthOptions: string[] = [];
  endYearOptions: string[] = [];
  fixedYearOptions: string[] = [];

  selectedStartWeek: SelectedDate = null;
  selectedEndWeek: SelectedDate = null;
  selectedStartMonth: SelectedDate = null;
  selectedEndMonth: SelectedDate = null;
  selectedStartYear: SelectedDate = null;
  selectedEndYear: SelectedDate = null;
  selectedYearRange: SelectedDate = null;

  isValid = true;

  weekText = this.translate.instant('module.shared.excelDownloadWizard.weekText');
  monthText = this.translate.instant('module.shared.excelDownloadWizard.monthText');
  yearText = this.translate.instant('module.shared.excelDownloadWizard.yearText');

  constructor(public translate: TranslateService) {}

  ngOnInit() {
    this.title.emit(this.translate.instant('module.shared.excelDownloadWizard.titleText3'));
    this.getDateSelectionOption();
    this.initializeDateFilter();
    this.getNextButtonStatus();
    this.reverseMonthOptions();
  }

  getNextButtonStatus() {
    const currentFilterGranularity = this.filter.granularity as MeterConsumptionGranularityKeys;
    const switchValue = MeterConsumptionGranularityTypes[currentFilterGranularity];
    switch (switchValue) {
      case MeterConsumptionGranularityTypes.fifteenMinute:
      case MeterConsumptionGranularityTypes.hourly:
        // case MeterConsumptionGranularityTypes.AbsoluteReading:
        !this.isStartDateEmpty && !this.isEndDateEmpty ? this.nextButtonStatus.emit(false) : this.nextButtonStatus.emit(true);
        break;

      case MeterConsumptionGranularityTypes.daily:
        !isNil(this.selectedStartMonth) &&
        !isNil(this.selectedEndMonth) &&
        !isNil(this.selectedStartYear) &&
        !isNil(this.selectedEndYear) &&
        this.selectedStartMonth !== this.monthText &&
        this.selectedEndMonth !== this.monthText &&
        this.selectedStartYear !== this.yearText &&
        this.selectedEndYear !== this.yearText
          ? this.nextButtonStatus.emit(false)
          : this.nextButtonStatus.emit(true);
        break;

      case MeterConsumptionGranularityTypes.weekly:
        !isNil(this.selectedStartWeek) &&
        !isNil(this.selectedEndWeek) &&
        !isNil(this.selectedStartYear) &&
        !isNil(this.selectedEndYear) &&
        this.selectedStartWeek !== this.weekText &&
        this.selectedEndWeek !== this.weekText &&
        this.selectedStartYear !== this.yearText &&
        this.selectedEndYear !== this.yearText
          ? this.nextButtonStatus.emit(false)
          : this.nextButtonStatus.emit(true);
        break;

      case MeterConsumptionGranularityTypes.monthly:
        !isNil(this.selectedStartYear) && !isNil(this.selectedEndYear) && this.selectedStartYear !== this.yearText && this.selectedEndYear !== this.yearText
          ? this.nextButtonStatus.emit(false)
          : this.nextButtonStatus.emit(true);
        break;

      case MeterConsumptionGranularityTypes.yearly:
      case MeterConsumptionGranularityTypes.periodic:
        !isNil(this.selectedYearRange) && this.selectedYearRange !== this.yearText ? this.nextButtonStatus.emit(false) : this.nextButtonStatus.emit(true);
        break;
    }
  }

  isEmpty(value: any) {
    return isNil(value) || value === '';
  }

  getMinDate() {
    return isNil(this.startDate) ? this.minDate : this.startDate;
  }

  getMaxDate() {
    return isNil(this.endDate) ? this.maxDate : this.endDate;
  }

  resetDateFilter() {
    const currentFilterGranularity = this.filter.granularity as MeterConsumptionGranularityKeys;
    const switchValue = MeterConsumptionGranularityTypes[currentFilterGranularity];
    switch (switchValue) {
      case MeterConsumptionGranularityTypes.fifteenMinute:
      case MeterConsumptionGranularityTypes.hourly:
        // case MeterConsumptionGranularityTypes.AbsoluteReading:
        this.filter.dateRange.startWeek = null;
        this.filter.dateRange.endWeek = null;
        this.filter.dateRange.startMonth = null;
        this.filter.dateRange.endMonth = null;
        this.filter.dateRange.startYear = null;
        this.filter.dateRange.endYear = null;
        break;
      case MeterConsumptionGranularityTypes.daily:
      case MeterConsumptionGranularityTypes.weekly:
      case MeterConsumptionGranularityTypes.monthly:
        this.filter.dateRange.startDate = null;
        this.filter.dateRange.endDate = null;
        break;
      case MeterConsumptionGranularityTypes.yearly:
      case MeterConsumptionGranularityTypes.periodic:
        this.filter.dateRange.startDate = null;
        this.filter.dateRange.endDate = null;
        this.filter.dateRange.startWeek = null;
        this.filter.dateRange.endWeek = null;
        this.filter.dateRange.startMonth = null;
        this.filter.dateRange.endMonth = null;
        break;
    }
  }

  getDateSelectionOption() {
    const currentFilterGranularity = this.filter.granularity as MeterConsumptionGranularityKeys;
    this.useDatePicker = false;
    this.useMonthYearDropdown = false;
    this.useWeekYearDropdown = false;
    this.useYearDropdown = false;
    this.useFixedYearRange = false;

    this.selectedStartWeek = null;
    this.selectedEndWeek = null;
    this.selectedStartMonth = null;
    this.selectedEndMonth = null;
    this.selectedStartYear = null;
    this.selectedEndYear = null;
    this.selectedYearRange = null;

    const switchValue = MeterConsumptionGranularityTypes[currentFilterGranularity];

    switch (switchValue) {
      case MeterConsumptionGranularityTypes.fifteenMinute:
      case MeterConsumptionGranularityTypes.hourly:
        // case MeterConsumptionGranularityTypes.AbsoluteReading:
        this.useDatePicker = true;
        break;

      case MeterConsumptionGranularityTypes.daily:
        this.useMonthYearDropdown = true;
        break;

      case MeterConsumptionGranularityTypes.weekly:
        this.useWeekYearDropdown = true;
        break;

      case MeterConsumptionGranularityTypes.monthly:
        this.useYearDropdown = true;
        break;

      case MeterConsumptionGranularityTypes.yearly:
      case MeterConsumptionGranularityTypes.periodic:
        this.useFixedYearRange = true;
        break;
    }
  }

  initializeWeekFilter() {
    let weeks = this.getWeeksInAYear(this.maxDate);
    this.startWeekOptions = weeks;
    this.endWeekOptions = weeks;

    if (!isNil(this.selectedStartYear) && this.selectedStartYear !== this.yearText) {
      this.startWeekOptions = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
      weeks = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
    }

    if (!isNil(this.selectedEndYear) && this.selectedEndYear !== this.yearText) {
      this.endWeekOptions = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
      weeks = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
    }

    if (!isNil(this.filter.dateRange.startYear) || !isNil(this.filter.dateRange.endYear)) {
      const x = weeks.indexOf(this.filter.dateRange.startWeek as string);
      const y = weeks.indexOf(this.filter.dateRange.endWeek as string);

      if (x >= y && (x !== -1 || y !== -1)) {
        this.isValid = true;
      }

      if (x < y && !isNil(this.filter.dateRange.startWeek) && this.filter.dateRange.startYear === this.filter.dateRange.endYear) {
        this.isValid = false;
        this.initializeYearFilter();
      }

      if (x === -1 && this.selectedStartYear === this.selectedEndYear && !isNil(this.selectedStartYear) && !isNil(this.selectedEndYear)) {
        this.startWeekOptions = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
        this.endWeekOptions = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
        this.selectedStartWeek = null;
        this.filter.dateRange.startWeek = null;
      }

      if (y === -1 && this.selectedEndYear === this.endYearOptions[0] && !isNil(this.selectedEndWeek)) {
        this.endWeekOptions = this.getWeeksInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
        this.selectedEndWeek = null;
        this.filter.dateRange.endWeek = null;
      }

      if (this.filter.dateRange.startYear === this.filter.dateRange.endYear && !isNil(this.filter.dateRange.startYear)) {
        weeks = this.getWeeksInAYear(moment(new Date(parseInt(this.filter.dateRange.startYear), 0, 1)));
        if (!isNil(this.filter.dateRange.startWeek)) {
          this.endWeekOptions = weeks.slice(0, weeks.indexOf(this.filter.dateRange.startWeek) + 1);
        }

        if (!isNil(this.filter.dateRange.endWeek)) {
          this.startWeekOptions = weeks.slice(weeks.indexOf(this.filter.dateRange.endWeek), weeks.length);
        }
      }

      if (!isNil(this.selectedStartWeek) && !isNil(this.selectedStartYear) && isNil(this.selectedEndYear)) {
        this.endWeekOptions = this.startWeekOptions.slice(0, this.startWeekOptions.indexOf(this.filter.dateRange.startWeek as string) + 1);
      }

      if (!isNil(this.selectedEndWeek) && !isNil(this.selectedEndYear) && isNil(this.selectedStartYear)) {
        this.startWeekOptions = this.endWeekOptions.slice(this.endWeekOptions.indexOf(this.filter.dateRange.endWeek as string), this.endWeekOptions.length);
      }
    }
  }

  getWeeksInAYear(date: moment.Moment) {
    const startDate = moment(this.minDate);

    const weeksInAYear = moment(new Date(date.year(), 1, 1)).isoWeeksInYear();
    const startWeek = startDate.year() === date.isoWeekYear() ? startDate.isoWeek() - 1 : 0;
    return map(range(weeksInAYear, startWeek), function (n) {
      return `KW ${n}`;
    });
  }

  initializeMonthFilter() {
    let months = this.getMonthsInAYear(moment(new Date(this.maxDate.year() - 1, 0, 1)));
    this.startMonthOptions = months;
    this.endMonthOptions = months;

    if (!isNil(this.selectedStartYear) && this.selectedStartYear !== this.yearText) {
      this.startMonthOptions = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
      months = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
    }

    if (!isNil(this.selectedEndYear) && this.selectedEndYear !== this.yearText) {
      this.endMonthOptions = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
      months = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
    }

    if (!isNil(this.filter.dateRange.startYear) || !isNil(this.filter.dateRange.endYear)) {
      const startMonth = months.indexOf(this.filter.dateRange.startMonth as string);
      const endMonth = months.indexOf(this.filter.dateRange.endMonth as string);

      this.isValid = startMonth >= endMonth && (startMonth !== -1 || endMonth !== -1);

      if (startMonth < endMonth && !isNil(this.filter.dateRange.startMonth) && this.filter.dateRange.startMonth === this.filter.dateRange.endMonth) {
        this.isValid = false;
        this.initializeYearFilter();
      }

      if (!isNil(this.selectedEndYear) && !isNil(this.selectedStartYear)) {
        if (startMonth === -1 && this.selectedStartYear === this.selectedEndYear) {
          this.startMonthOptions = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
          this.endMonthOptions = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
          this.selectedStartMonth = null;
          this.filter.dateRange.startMonth = null;
        }

        if (endMonth === -1 && this.selectedEndYear === this.endYearOptions[0] && !isNil(this.selectedEndMonth)) {
          this.startMonthOptions = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedStartYear), 0, 1)));
          this.endMonthOptions = this.getMonthsInAYear(moment(new Date(parseInt(this.selectedEndYear), 0, 1)));
          this.selectedEndMonth = null;
          this.filter.dateRange.endMonth = null;
        }
      }

      if (this.filter.dateRange.startYear === this.filter.dateRange.endYear && !isNil(this.filter.dateRange.startYear)) {
        months = this.getMonthsInAYear(moment(new Date(parseInt(this.filter.dateRange.startYear), 0, 1)));
        if (!isNil(this.filter.dateRange.startMonth)) {
          this.endMonthOptions = months.slice(0, months.indexOf(this.filter.dateRange.startMonth) + 1);
        }

        if (!isNil(this.filter.dateRange.endMonth)) {
          this.startMonthOptions = months.slice(months.indexOf(this.filter.dateRange.endMonth), months.length);
        }
      }

      if (
        months.indexOf(this.selectedStartMonth as string) < months.indexOf(this.selectedEndMonth as string) &&
        !isNil(this.selectedStartMonth) &&
        !isNil(this.selectedEndMonth)
      ) {
        this.isValid = false;
        this.initializeYearFilter();
      }
    }
  }

  getMonthsInAYear(date: moment.Moment) {
    const endDate = moment(this.maxDate);
    const startDate = moment(this.minDate);

    const monthsInAYear = startDate.year() === date.year() || endDate.year() === date.year() ? endDate.month() + 1 : 12;
    return map(range(monthsInAYear, 0), function (n) {
      return moment()
        .month(n - 1)
        .format('MMM');
    });
  }

  reverseMonthOptions() {
    this.startMonthOptions = this.startMonthOptions.map(this.startMonthOptions.pop, [...this.startMonthOptions]) as string[];
    this.endMonthOptions = this.endMonthOptions.map(this.endMonthOptions.pop, [...this.endMonthOptions]) as string[];
  }

  initializeYearFilter() {
    const currentFilterGranularity = this.filter.granularity as MeterConsumptionGranularityKeys;
    const getYears = (index: number) => {
      const years: string[] = [];
      const dateStart = moment().subtract(5, 'y');
      const dateEnd = moment();

      while (dateEnd.diff(dateStart, 'years') >= 0) {
        years.push(dateStart.format('YYYY'));
        dateStart.add(1, 'year');
      }

      for (let i = 0; i < years.length; i++) {
        if (years[i] === moment().add(1, 'y').format('YYYY')) {
          years.splice(i, 1);
        }
      }

      this.startYearOptions = years;
      this.endYearOptions = years;

      if (!isNil(this.filter.dateRange.startYear)) {
        this.endYearOptions = years.slice(years.indexOf(this.filter.dateRange.startYear) + index, years.length);
        this.endYearOptions.sort((a: any, b: any) => b - a);
      }

      if (!isNil(this.filter.dateRange.endYear)) {
        years.sort((a: any, b: any) => b - a);
        this.startYearOptions = years.slice(years.indexOf(this.filter.dateRange.endYear) + index, years.length);
      }

      this.startYearOptions.sort((a: any, b: any) => b - a);
      this.endYearOptions.sort((a: any, b: any) => b - a);
    };

    getYears(0);

    if (MeterConsumptionGranularityTypes[currentFilterGranularity] === MeterConsumptionGranularityTypes.weekly) {
      getYears(0);

      if (
        (!isNil(this.filter.dateRange.startWeek) || !isNil(this.filter.dateRange.endWeek)) &&
        this.filter.dateRange.startWeek === this.filter.dateRange.endWeek
      ) {
        getYears(0);
      }

      if (!this.isValid) {
        getYears(1);
      }

      if (!isNil(this.filter.dateRange.startWeek) && this.endYearOptions.length === 1 && this.isValid) {
        this.endWeekOptions = this.endWeekOptions.slice(0, this.endWeekOptions.indexOf(this.filter.dateRange.startWeek) + 1);
      }
    }

    if (MeterConsumptionGranularityTypes[currentFilterGranularity] === MeterConsumptionGranularityTypes.daily) {
      getYears(0);

      if (
        (!isNil(this.filter.dateRange.startMonth) || !isNil(this.filter.dateRange.endMonth)) &&
        this.filter.dateRange.startMonth === this.filter.dateRange.endMonth
      ) {
        getYears(0);
      }

      if (!this.isValid) {
        getYears(1);
      }

      if (!isNil(this.filter.dateRange.startMonth) && this.endYearOptions.length === 1 && this.isValid) {
        this.endMonthOptions = this.endMonthOptions.slice(0, this.endMonthOptions.indexOf(this.filter.dateRange.startMonth) + 1);
      }
    }
  }

  initializeFixedYearRange() {
    const yearRange = rangeRight(this.maxDate.year() - 5, this.maxDate.year() + 1);
    this.fixedYearOptions.push(`${last(yearRange)}-${first(yearRange)}`);
  }

  initializeDateFilter() {
    this.initializeWeekFilter();
    this.initializeMonthFilter();
    this.initializeYearFilter();
    this.initializeFixedYearRange();

    this.selectedStartWeek = isNil(this.filter.dateRange.startWeek) ? this.weekText : this.filter.dateRange.startWeek;
    this.selectedEndWeek = isNil(this.filter.dateRange.endWeek) ? this.weekText : this.filter.dateRange.endWeek;
    this.selectedStartMonth = isNil(this.filter.dateRange.startMonth) ? this.monthText : this.filter.dateRange.startMonth;
    this.selectedEndMonth = isNil(this.filter.dateRange.endMonth) ? this.monthText : this.filter.dateRange.endMonth;
    this.selectedStartYear = isNil(this.filter.dateRange.startYear) ? this.yearText : this.filter.dateRange.startYear;
    this.selectedEndYear = isNil(this.filter.dateRange.endYear) ? this.yearText : this.filter.dateRange.endYear;
    this.selectedYearRange =
      !isNil(this.filter.dateRange.startYear) && !isNil(this.filter.dateRange.endYear)
        ? `${this.filter.dateRange.startYear}-${this.filter.dateRange.endYear}`
        : this.yearText;
  }

  selectStartWeek(value: string) {
    this.selectedStartWeek = value;
    this.filter.dateRange.startWeek = value;
    this.initializeWeekFilter();
    this.initializeYearFilter();
    this.getNextButtonStatus();
  }

  selectEndWeek(value: string) {
    this.selectedEndWeek = value;
    this.filter.dateRange.endWeek = value;
    this.initializeWeekFilter();
    this.initializeYearFilter();
    this.getNextButtonStatus();
  }

  selectStartMonth(value: string) {
    this.selectedStartMonth = value;
    this.filter.dateRange.startMonth = value;
    this.initializeMonthFilter();
    this.initializeYearFilter();
    this.reverseMonthOptions();
    this.getNextButtonStatus();
  }

  selectEndMonth(value: string) {
    this.selectedEndMonth = value;
    this.filter.dateRange.endMonth = value;
    this.initializeMonthFilter();
    this.initializeYearFilter();
    this.reverseMonthOptions();
    this.getNextButtonStatus();
  }

  selectStartYear(value: string) {
    this.selectedStartYear = value;
    this.filter.dateRange.startYear = value;
    this.initializeWeekFilter();
    this.initializeMonthFilter();
    this.initializeYearFilter();
    this.reverseMonthOptions();
    this.getNextButtonStatus();
  }

  selectEndYear(value: string) {
    this.selectedEndYear = value;
    this.filter.dateRange.endYear = value;
    this.initializeWeekFilter();
    this.initializeMonthFilter();
    this.initializeYearFilter();
    this.reverseMonthOptions();
    this.getNextButtonStatus();
  }

  selectFixedYearRange(value: string) {
    this.selectedYearRange = value;
    const years = value.split('-');
    this.filter.dateRange.startYear = years[0];
    this.filter.dateRange.endYear = years[1];
    this.getNextButtonStatus();
  }
}

type SelectedDate = string | null;
